import type { SelectMultiChoicelistOption } from 'components/shared/select-multi-choicelist/SelectMultiChoicelist';
import { PackageProviderName } from 'data/ppoints/constants';
import { MappingCountryMarkets } from 'features/shipping-provider/settings/hooks/useMappingCountryMarkets';
import {
    ShippingRateMappingForm,
    useProviderRateMappingData,
} from 'features/shipping-provider/settings/hooks/useProviderRateMappingData';
import { useEffect, useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { MarketRateGroupForm } from './MarketRateStep';

export interface MarketRateStepProviderData {
    withMarket: MappingCountryMarkets[];
    withoutMarket: MappingCountryMarkets[];
    getRates(countryCode: string): number[];
    setRates(countryCode: string, rateIds: number[]): void;
    getOptions(countryCode: string): SelectMultiChoicelistOption<number>[];
    error?: Error;
    optionsError?: Error;
    mutate: () => void;
    mutateOptions: () => void;
    defaults: ShippingRateMappingForm;
    defaultsSignature: string;
    valuesSignature: string;
    optionsSignature: string;
    save: (data: ShippingRateMappingForm) => Promise<void>;
    isInitialLoading: boolean;
}

interface MarketRateStepProviderSectionProps {
    provider: PackageProviderName;
    form: UseFormReturn<MarketRateGroupForm>;
    onDataChange: (provider: PackageProviderName, data: MarketRateStepProviderData) => void;
}

/**
 * Headless — fetches one provider's country/rate mapping (`useProviderRateMappingData`, pointed at
 * this provider's slice of `MarketRateStep`'s shared form) and reports the whole result up via
 * `onDataChange` instead of rendering anything itself.
 *
 * `useProviderRateMappingData` can only be called once per provider (the countries it returns are
 * provider-specific), so a call per provider still has to happen somewhere — this component is that
 * somewhere. `MarketRateStep` then regroups every provider's report by country with
 * `useMarketRateCountryGroups` and renders from that, not from this component's own output — a
 * country card needs every provider active in it side by side, which this component alone can't see.
 */
export const MarketRateStepProviderSection: React.FC<MarketRateStepProviderSectionProps> = ({
    provider,
    form,
    onDataChange,
}) => {
    const data = useProviderRateMappingData(
        provider,
        form as unknown as UseFormReturn<Record<string, unknown>>,
        (field) => `providers.${provider}.${field}`
    );

    const onDataChangeRef = useRef(onDataChange);
    onDataChangeRef.current = onDataChange;

    // `withMarket`/`withoutMarket` can get a fresh array identity on a render that didn't actually
    // change which countries are in them (e.g. a revalidation that resolves to the same content) —
    // keying the effect below on their identity directly re-fired on every such render, which fed
    // straight back into a parent state update and looped. This content signature only changes when
    // the actual country list does.
    const countriesSignature = useMemo(
        () =>
            JSON.stringify([
                data.withMarket.map((market) => market.countryCode),
                data.withoutMarket.map((market) => market.countryCode),
            ]),
        [data.withMarket, data.withoutMarket]
    );

    // `data` itself is a fresh object every render — only the fields that actually determine whether
    // anything downstream changed are listed here. Errors are compared by message rather than object
    // identity for the same reason as `countriesSignature` — some sources hand back a new but
    // equivalent Error instance on renders that didn't really change anything. `valuesSignature` is
    // what makes a rate/type selection actually reach `MarketRateStep` — none of the other fields
    // change when the merchant just picks a rate, so leaving it out silently freezes the selectors.
    // `optionsSignature` does the same for the selectable rates — without it a `mutateOptions` refetch
    // (e.g. after rates are added in Shopify) never reaches `MarketRateStep`, and the lists stay stale.
    useEffect(() => {
        onDataChangeRef.current(provider, data);
    }, [
        provider,
        countriesSignature,
        data.error?.message,
        data.optionsError?.message,
        data.defaultsSignature,
        data.valuesSignature,
        data.optionsSignature,
        data.isInitialLoading,
    ]);

    return null;
};
